import React, {useEffect, useState} from "react";
import styles from "./SocietyHomepageArticles.module.css";
import Typography from "@mui/material/Typography";
import ArticleCard from "../../articleCard/ArticleCard";
import {Button, Grid2} from "@mui/material";
import useSociety from "../../../hooks/useSociety";
import AddArticleModal from "../../societyAdmin/societyAdminArticles/addArticleModal/AddArticleModal";
import {ProductDTO} from "../../societyAdmin/societyAdminArticles/societyAdminArticlesList/SocietyAdminArticlesList";
import {useNavigate} from "react-router-dom";
import useDataFetch from "../../../hooks/useDataFetch";
import Divider from "@mui/material/Divider";

export interface ConnectedAccountStateDTO {
    accountId: string,
    chargesEnabled: boolean,
    payoutsEnabled: boolean,
    detailsSubmitted: boolean,
    hasPendingRequirements: boolean,
    hasPastDueRequirements: boolean,
    hasErrors: boolean,
    hasEventuallyDueRequirements: boolean,
}

interface SocietyHomepageArticles {
    articles: ProductDTO[]
}

const SocietyHomepageArticles = ({articles}: SocietyHomepageArticles) => {

    const {society} = useSociety();

    const [open, setOpen] = useState<boolean>(false);
    const [articlesToDisplay, setArticlesToDisplay] = useState<ProductDTO[]>([]);
    const [connectedAccountState, setConnectedAccountState] = useState<ConnectedAccountStateDTO>();
    const [isConnectedAccountReady, setIsConnectedAccountReady] = useState<boolean>(false);
    const [hasConnectedAccount, setHasConnectedAccount] = useState<boolean>(false);
    const [hasDetailsSubmitted, setHasDetailsSubmitted] = useState<boolean>(false);

    const navigate = useNavigate();

    const {
        fetchDataPromise: retrieveConnectedAccountState,
        isLoading
    } = useDataFetch<ConnectedAccountStateDTO>(`/stripe/connected-account/retrieve-connnected-account`, true, false);

    useEffect(() => {
        if (society.secured) {
            retrieveConnectedAccountState(`/stripe/connected-account/retrieve-connnected-account`).then((response) => {
                if (response.data) {
                    setConnectedAccountState(response.data);
                }
            });
        }
    }, [society]);

    useEffect(() => {
        if (!isLoading && connectedAccountState) {
            if (connectedAccountState.detailsSubmitted) {
                setHasDetailsSubmitted(true);
            }
            if (connectedAccountState.accountId) {
                setHasConnectedAccount(true);
                if (connectedAccountState.chargesEnabled && connectedAccountState.payoutsEnabled && connectedAccountState.detailsSubmitted
                    && !connectedAccountState.hasPendingRequirements && !connectedAccountState.hasPastDueRequirements) {
                    setIsConnectedAccountReady(true);
                }
            }
        }
    }, [isLoading, connectedAccountState]);

    useEffect(() => {
        if (articles && !isLoading) {
            const starArticles = articles.filter((article: ProductDTO) => article.star);
            if (starArticles.length > 0) {
                setArticlesToDisplay(starArticles);
            } else {
                setArticlesToDisplay(articles.slice(0, 4))
            }
        }
    }, [articles, isLoading]);

    return (
        <>
            <div className={styles.content}>
                <Typography
                    sx={{
                        color: '#ffffff',
                        fontSize: {xs: 30, sm: 40},
                    }}
                    variant={"firstVariant"}
                >
                    ARTICLES
                </Typography>

                {society.secured && !isLoading && (!hasConnectedAccount || !hasDetailsSubmitted) && (
                    <>
                        <Typography
                            textAlign={"left"}
                            variant={"secondVariant"}
                            sx={{mt: 2}}
                        >
                            Avant de rendre visibles vos articles à vos futurs clients, nous devons vérifier votre identité. Cela se fait en
                            quelques clics via notre partenaire Stripe. Cliquez sur le bouton ci-dessous pour commencer.
                        </Typography>

                        <Button
                            variant={"firstVariant"}
                            sx={{mt: 2, mb: 2}}
                            onClick={() => navigate('/stripe')}
                        >
                            Vérifier mon identité.
                        </Button>
                    </>
                )}

                {society.secured && hasConnectedAccount && hasDetailsSubmitted && !isConnectedAccountReady && (
                    <>
                        <Typography
                            textAlign={"left"}
                            variant={"secondVariant"}
                        >
                            Votre compte n'est pas complet. Vos clients ne verront pas encore vos articles pour le
                            moment mais vous pouvez déjà les créer. Veuillez vérifier que vous avez bien rempli toutes
                            les informations nécessaires.
                        </Typography>
                        <Button
                            variant={"firstVariant"}
                            sx={{mt: 2, mb: 2}}
                        >
                            Compléter mon compte Stripe
                        </Button>
                    </>
                )}

                {!isLoading && (
                    <>
                        {society.secured && articlesToDisplay && articlesToDisplay.length === 0 && (
                            <Typography
                                variant={"fourthVariant"}
                                fontSize={{xs: 20, sm: 25}}
                            >
                                Vous seul voyez cette section tant que vous n'avez pas ajouté au moins un article.
                            </Typography>
                        )}

                        {articlesToDisplay && articlesToDisplay.length === 1 && (
                            <Grid2 container>
                                <Grid2 size={{xs: 0, sm: 3, md: 4}}></Grid2>
                                <Grid2 container size={{xs: 12, sm: 6, md: 4}} spacing={6} mt={2}>
                                    {articlesToDisplay.map((article: ProductDTO) => (
                                        <Grid2
                                            key={article.name! + article.id}
                                            size={12}
                                        >
                                            <ArticleCard key={article.id} article={article}/>
                                        </Grid2>
                                    ))}
                                </Grid2>
                                <Grid2 size={{xs: 0, sm: 3, md: 4}}></Grid2>
                            </Grid2>
                        )}

                        {articlesToDisplay && articlesToDisplay.length === 2 && (
                            <Grid2 container>
                                <Grid2 size={{xs: 0, sm: 0, md: 2}}></Grid2>
                                <Grid2 container size={{xs: 12, sm: 12, md: 8}} spacing={6} mt={2}>
                                    {articlesToDisplay.map((article: ProductDTO) => (
                                        <Grid2
                                            key={article.name! + article.id}
                                            size={{xs: 12, sm: 6, md: 6}}
                                        >
                                            <ArticleCard key={article.id} article={article}/>
                                        </Grid2>
                                    ))}
                                </Grid2>
                                <Grid2 size={{xs: 0, sm: 0, md: 2}}></Grid2>
                            </Grid2>
                        )}

                        {articlesToDisplay && articlesToDisplay.length > 2 && (
                            <Grid2 container spacing={6} mt={2}>
                                {articlesToDisplay.map((article: ProductDTO) => (
                                    <Grid2
                                        key={article.name! + article.id}
                                        size={{xs: 12, sm: 6, md: 6, lg: 3}}
                                    >
                                        <ArticleCard key={article.id} article={article}/>
                                    </Grid2>
                                ))}
                            </Grid2>
                        )}

                        <Grid2 container>
                            <Grid2 size={12}>
                                {society.secured && (
                                    <Button
                                        sx={{
                                            mt: 4,
                                        }}
                                        variant={"fourthVariant"}
                                        onClick={() => setOpen(true)}
                                    >
                                        Ajouter un article
                                    </Button>
                                )}
                            </Grid2>
                            <Grid2 size={12}>
                                <Button
                                    sx={{
                                        mt: 6,
                                        fontSize: {xs: 16, sm: 18, md: 20},
                                    }}
                                    variant="fourthVariant"
                                    onClick={() => navigate('/articles')}
                                >
                                    Voir tous les articles
                                </Button>
                            </Grid2>
                        </Grid2>
                        <AddArticleModal open={open} setOpen={setOpen}/>
                    </>
                )}
            </div>
        </>
    );
}

export default SocietyHomepageArticles
